import React, { useEffect, useState } from "react";
import { useAppDispatch as useDispatch } from "src/core/redux/hooks";
import { setPageTitle, removeInfoMessage } from "src/core/redux";
import { SentinelPageProps } from "src/web/interfaces";
import { SentinelBasePage } from "src/web/pages/sentinel-base";
import {
    Box,
    Button,
    Header,
    Pagination,
    Popover,
    SpaceBetween,
    StatusIndicator,
    Table,
    Link,
    TextFilter,
    Alert,
} from "@cloudscape-design/components";
import { getPackageDependencyList } from "src/core/sentinel/actions";
import { SentinelController } from "src/core/controller";
import { PackageDependency } from "src/core/sentinel/types";
import { isMajorVersionUpgrade } from "src/core/util/version";

import { useParams, useNavigate } from "react-router-dom";
export interface SentinelPackageDetailPageProps extends SentinelPageProps {
  readOnly?: boolean;
  sentinelController: SentinelController;
}
export const PackageDetailPage: React.FC<SentinelPackageDetailPageProps> = (
    // eslint-disable-next-line
  props: SentinelPackageDetailPageProps
) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [filteringText, setFilteringText] = useState("");
    const [currentPageIndex, setCurrentPageIndex] = useState(1);
    const [dependencyList, setDependencyList] = useState<PackageDependency[]>([]);
    const pageSize = 50;
    const { packageId } = useParams<{ packageId: string }>();
    useEffect(() => {
        const fetchDependencyList = async () => {
            setError("");
            if (!packageId) return;
            try {
                const response: any = await dispatch(
                    getPackageDependencyList({
                        sentinelController: props.sentinelController,
                        packageDependencyListInput: { packageName: packageId },
                    })
                ).unwrap();
                const list = response?.payload?.packageDependencyList;
                setDependencyList(Array.isArray(list) ? list : []);
            } catch (error) {
                setError("Failed to load package dependencies.");
            }
            setLoading(false);
        };
        fetchDependencyList();
    }, [packageId]);

    const filteredPackages = dependencyList.filter((d) => {
        const content = [
            d.dependencyName,
            d.version,
            d.vendorGuidanceStatus,
            d.recommendedVersion,
            d.riskStatus,
        ]
            .join(" ")
            .toLowerCase();

        return !filteringText || content.includes(filteringText.toLowerCase());
    });

    const paginatedItems = filteredPackages.slice(
        (currentPageIndex - 1) * pageSize,
        currentPageIndex * pageSize
    );

    const totalPages = Math.ceil(filteredPackages.length / pageSize);

    useEffect(() => {
        dispatch(setPageTitle("Package Detail"));
        dispatch(removeInfoMessage({ id: "message_1" }));
    }, []);

    return (
        <SentinelBasePage>
            {error && <Alert type="error" dismissible onDismiss={() => setError("")}>{error}</Alert>}
            <Table
                items={paginatedItems}
                columnDefinitions={[
                    {
                        id: "dependencyName",
                        header: "Dependency Name",
                        cell: (e: PackageDependency) => e.dependencyName || "-",
                        sortingField: "dependencyName",
                    },
                    {
                        id: "version",
                        header: "Current Version",
                        cell: (e: PackageDependency) => e.version || "-",
                        sortingField: "version",
                    },
                    {
                        id: "vendorGuidanceStatus",
                        header: "Current Version Status",
                        cell: (e: PackageDependency) => {
                            const status = e.vendorGuidanceStatus || "-";
                            if (
                                status === "END_OF_LIFE" ||
                status === "DEPRECATED" ||
                status === "RECALLED"
                            ) {
                                return <StatusIndicator type="error">{status}</StatusIndicator>;
                            }
                            if (status === "AT_RISK" || status === "UNSTABLE" || status === "EXPERIMENTAL") {
                                return <StatusIndicator type="warning">{status}</StatusIndicator>;
                            }
                            return status;
                        },
                        sortingField: "vendorGuidanceStatus",
                    },
                    {
                        id: "recommendedVersion",
                        header: "Vendor Guidance Recommended Version",
                        cell: (e: PackageDependency) => {
                            if (e.riskStatus === "HIGH_RISK") {
                                return <Link external href={`https://code.amazon.com/packages/${e.dependencyName}/releases`}>Look for other upgradable alternatives</Link>;
                            }
                            const version = e.recommendedVersion;
                            if (!version) return "Not Available";
                            if (e.riskStatus === "LOW_RISK" && isMajorVersionUpgrade(e.version, version)) {
                                return (
                                    <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
                                        <span>{version}</span>
                                        <Popover
                                            header="Major version upgrade"
                                            size="medium"
                                            triggerType="custom"
                                            content={
                                                <SpaceBetween size="xs">
                                                    <Box>
                                                        Upgrading <b>{e.dependencyName}</b> from <b>{e.version}</b> to{" "}
                                                        <b>{version}</b> crosses a major version boundary.
                                                    </Box>
                                                    <Box>
                                                        Dependency Sentinel currently only automates minor version upgrades. No code 
                                                        review will be raised for this dependency. A manual upgrade is required.
                                                    </Box>
                                                    <Box>Support for major version upgrades is coming soon.</Box>
                                                </SpaceBetween>
                                            }
                                        >
                                            <Button
                                                variant="inline-link"
                                                iconName="status-warning"
                                                ariaLabel={`Upgrading ${e.dependencyName} from ${e.version} to ${version} is a major version upgrade, which is not supported yet`}
                                            />
                                        </Popover>
                                    </SpaceBetween>
                                );
                            }
                            return version;
                        },
                        sortingField: "recommendedVersion",
                    },
                    {
                        id: "riskStatus",
                        header: (
                            <SpaceBetween direction="horizontal" size="xs">
                                <span>Risk</span>
                                <Popover
                                    header="Risk Levels"
                                    content={
                                        <Box>
                                            <p>
                                                <b>HIGH_RISK</b>: Current version has negative guidance
                        and no upgrade is available.
                                            </p>
                                            <p>
                                                <b>AT_RISK</b>: Current version has negative guidance
                        but an upgrade is available.
                                            </p>
                                            <p>
                                                <b>UPGRADE_AVAILABLE</b>: Current version is fine but a
                        newer recommended version exists.
                                            </p>
                                            <p>
                                                <b>NO_ACTION_REQUIRED</b>: Current version is fine with
                        no upgrade needed.
                                            </p>
                                        </Box>
                                    }
                                    triggerType="custom"
                                >
                                    <Button variant="inline-link" iconName="status-info" />
                                </Popover>
                            </SpaceBetween>
                        ),
                        cell: (e: PackageDependency) => {
                            const risk = e.riskStatus || "-";
                            if (risk === "HIGH_RISK") {
                                return <StatusIndicator type="error"><b>HIGH_RISK</b></StatusIndicator>;
                            }
                            if (risk === "LOW_RISK") {
                                return <span style={{ color: "#ff8c00", fontWeight: "bold" }}>⚠ AT_RISK</span>;
                            }
                            if (risk === "UPGRADE_AVAILABLE") {
                                return <StatusIndicator type="info">{risk}</StatusIndicator>;
                            }
                            if (risk === "NO_ACTION_REQUIRED") {
                                return (
                                    <StatusIndicator type="success">{risk}</StatusIndicator>
                                );
                            }
                            return risk;
                        },
                        sortingField: "riskStatus",
                    },
                ]}
                resizableColumns
                stickyHeader
                renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
                    `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
                }
                loading={loading}
                loadingText="Loading resources"
                empty={
                    <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
                        <SpaceBetween size="m">
                            <b>No dependency found</b>
                        </SpaceBetween>
                    </Box>
                }
                filter={
                    <TextFilter
                        filteringPlaceholder="Search dependencies..."
                        filteringText={filteringText}
                        onChange={({ detail }) => {
                            setFilteringText(detail.filteringText);
                            setCurrentPageIndex(1);
                        }}
                        countText={`${filteredPackages.length} matches`}
                    />
                }
                header={
                    <Header
                        counter={`(${filteredPackages.length})`}
                        actions={
                            <Button variant="link" onClick={() => navigate(-1)}>
                ← Back to Packages
                            </Button>
                        }
                    >
            Dependencies for package: {packageId}
                    </Header>
                }
                pagination={
                    <Pagination
                        currentPageIndex={currentPageIndex}
                        pagesCount={totalPages}
                        onChange={({ detail }) =>
                            setCurrentPageIndex(detail.currentPageIndex)
                        }
                    />
                }
            />
        </SentinelBasePage>
    );
};
